import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useScrollReset } from "utils";
import { AboutUsIcon } from "../../../components/Icons";
import FounderSection from "./FounderSection";

gsap.registerPlugin(ScrollTrigger);

export default function AboutUs() {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sectionsRef = useRef<HTMLDivElement[]>([]);
  const ctxRef = useRef<gsap.Context | null>(null);

  useScrollReset();

  useEffect(() => {
    ScrollTrigger.refresh();
  }, []);

  useEffect(() => {
    const sections = sectionsRef.current;
    const container = containerRef.current;

    if (!container || sections.length === 0) return undefined;

    const timer = setTimeout(() => {
      ctxRef.current = gsap.context(() => {
        gsap.set(sections, {
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          opacity: 0,
        });

        gsap.set(sections[0], { opacity: 1 });

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: container,
            start: "top top",
            end: "+=4000",
            scrub: true,
            pin: true,
            anticipatePin: 1,
            pinSpacing: true,
          },
        });

        sections.forEach((section, i) => {
          if (i !== 0) {
            tl.to(
              sections[i - 1],
              { y: -600, opacity: 0, duration: 1, ease: "power2.inOut" },
              `section${i}`,
            ).fromTo(
              sections[i],
              { y: 600, opacity: 0 },
              { y: 0, opacity: 1, duration: 1, ease: "power2.inOut" },
              `section${i}`,
            );
          }
        });
      }, container);
    }, 50);

    return () => {
      clearTimeout(timer);
      ScrollTrigger.getAll().forEach((st) => st.kill(true));
      if (ctxRef.current) {
        ctxRef.current.revert();
        ctxRef.current = null;
      }
      ScrollTrigger.clearScrollMemory();
      window.removeEventListener("resize", ScrollTrigger.update);
    };
  }, []);

  const addToRefs = (el: HTMLDivElement | null) => {
    if (el && !sectionsRef.current.includes(el)) {
      sectionsRef.current.push(el);
    }
  };

  return (
    <div ref={wrapperRef}>
      <Helmet>
        <title>
          About Us | GridKey - The Vision Behind Modern Wealth Management
        </title>
        <meta
          name="description"
          content="Learn about GridKey — founded by finance professionals and technology experts to simplify wealth management with clarity and confidence."
        />
        <meta property="og:title" content="About Us | GridKey" />
        <meta
          property="og:description"
          content="GridKey was built to solve a clear problem: advisors needed one reliable platform to track and manage multi-asset portfolios across multiple clients and families."
        />
        <meta property="og:url" content="https://gridkey.in/about-us" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://gridkey.in/about-us" />
      </Helmet>
      <div
        ref={containerRef}
        className="relative h-screen w-full overflow-hidden text-white font-['Inter']"
        style={{
          background:
            "radial-gradient(circle,rgba(0, 40, 51, 1) 0%, rgba(0, 0, 0, 1) 60%)",
        }}
      >
        {/* SECTION 1 */}
        <div
          ref={addToRefs}
          className="h-full flex flex-col justify-center items-center text-center px-4 mt-10"
        >
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-medium leading-tight md:leading-[72.8px] max-w-[900px]">
            Gridkey is the command center for modern wealth
          </h1>
          <AboutUsIcon className="mt-8 w-full max-w-[678px] h-auto object-contain" />
        </div>

        {/* SECTION 2 */}
        <div
          ref={addToRefs}
          className="h-full flex flex-col justify-center items-center text-center px-6 md:px-16"
        >
          <div className="w-[578px] inline-flex flex-col justify-start items-center gap-8">
            <div className="self-stretch justify-start text-white text-4xl font-medium font-['Inter'] leading-[52px]">
              The Vision Behind Gridkey
            </div>
            <div className="self-stretch h-[559px] justify-start">
              <span className="text-grey-colors-grey-300 text-2xl font-medium font-['Inter'] leading-10">
                At{" "}
              </span>
              <span className="text-sky-800 text-2xl font-medium font-['Inter'] leading-10">
                Gridkey
              </span>
              <span className="text-grey-300 text-2xl font-medium font-['Inter'] leading-10 text-justify">
                , we simplify wealth management with technology that empowers
                smarter financial decisions. Our vision is to help businesses
                and individuals manage wealth with clarity and confidence.
                <br />
                Built on innovation, integrity, and excellence, our platform
                delivers tools that transform the way wealth management
                operates.
                <br />
                Founded by finance professionals, technology experts, and
                entrepreneurs, Gridkey combines deep experience in finance,
                technology, and business development to create solutions that
                drive measurable results.
                <br />
              </span>
            </div>
          </div>
        </div>

        {/* SECTION 3 */}
        <div
          ref={addToRefs}
          className="h-full flex flex-col justify-center items-center text-center px-4 md:px-16"
        >
          <div className="w-[578px] inline-flex flex-col justify-start items-center gap-4">
            <div className="self-stretch justify-start text-white text-4xl font-medium font-['Inter'] leading-[52px]">
              Why We Built Gridkey
            </div>
            <div className="self-stretch justify-start text-grey-300 text-2xl font-medium font-['Inter'] leading-10 text-justify">
              Gridkey was founded to solve a clear problem in wealth management.
              Advisors needed one reliable platform to track and manage
              multi-asset portfolios across multiple clients and families.
              <br />
              <br />
              The tools available were slow, fragmented, and unable to match the
              pace of modern finance. Gridkey was built to change that.
              <br />
              <br />
              Our platform streamlines operations, empowers advisors with
              control, and delivers a seamless experience for clients. What
              began as a gap in the industry has become our mission: making
              wealth management simpler, smarter, and built for growth.
            </div>
          </div>
        </div>
      </div>

      <FounderSection />
    </div>
  );
}
